import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowRight } from 'lucide-react';
import { useUpdateClientStage } from '@/hooks/useClients';
import {
  ACTIVE_STAGES, PIPELINE_LABELS, CLIENT_TYPE_LABELS,
  type Client, type PipelineStage,
} from '@/types/clients';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';

type Props = { clients: Client[] };

export function PipelineView({ clients }: Props) {
  const { user } = useAuth();
  const updateStage = useUpdateClientStage();
  const canWrite = user ? can(user.role, 'clients', 'write') : false;

  const grouped = useMemo(() => {
    const acc: Record<PipelineStage, Client[]> = {
      lead: [], qualified: [], specified: [], quoted: [], awarded: [],
      closed_won: [], closed_lost: [], dormant: [],
    };
    for (const c of clients) (acc[c.stage] ||= []).push(c);
    return acc;
  }, [clients]);

  function stageValue(stage: PipelineStage) {
    return grouped[stage].reduce((sum, c) => sum + (c.estimatedAnnualValue ?? 0), 0);
  }

  return (
    <div className="overflow-x-auto -mx-8 px-8 pb-2">
      <div className="flex gap-3 min-w-max">
        {ACTIVE_STAGES.map((stage) => {
          const items = grouped[stage];
          return (
            <div key={stage} className="w-72 shrink-0">
              <div className="px-3 py-2.5 bg-bg-subtle border border-border rounded-t-sm">
                <div className="flex items-baseline justify-between mb-1">
                  <div className="kicker">{PIPELINE_LABELS[stage]}</div>
                  <span className="text-2xs text-text-dim tabular">{items.length}</span>
                </div>
                <div className="text-sm font-display text-brass-300 tabular">
                  {stageValue(stage) > 0 ? formatCurrency(stageValue(stage)) : '—'}
                </div>
              </div>

              <div className="bg-bg-subtle/40 border border-t-0 border-border rounded-b-sm p-2 min-h-[24rem] space-y-2">
                {items.length === 0 ? (
                  <div className="flex items-center justify-center h-32 text-2xs text-text-dim border border-dashed border-border rounded-sm">
                    No accounts
                  </div>
                ) : (
                  items.map((c) => (
                    <PipelineCard
                      key={c.id}
                      client={c}
                      canWrite={canWrite}
                      onAdvance={() => {
                        const next = nextStage(c.stage);
                        if (next) updateStage.mutate({ id: c.id, stage: next });
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PipelineCard({
  client, canWrite, onAdvance,
}: { client: Client; canWrite: boolean; onAdvance: () => void }) {
  const next = nextStage(client.stage);
  return (
    <div className="group relative bg-bg-panel border border-border rounded-sm p-3 hover:border-border-strong transition-colors">
      {/* Brass corner accent */}
      <div className="absolute top-0 left-0 w-4 h-px bg-brass-400/40" />
      <div className="absolute top-0 left-0 h-4 w-px bg-brass-400/40" />

      <Link to={`/clients/${client.id}`} className="block">
        <div className="flex items-start gap-2.5">
          <div className="shrink-0 h-7 w-7 rounded-sm bg-walnut-700/60 flex items-center justify-center">
            <Building2 size={12} className="text-brass-300" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-text leading-tight truncate">{client.name}</div>
            <div className="kicker text-[0.6rem] mt-1">{CLIENT_TYPE_LABELS[client.type]}</div>
          </div>
        </div>

        {client.estimatedAnnualValue ? (
          <div className="mt-3 pt-2.5 border-t border-border flex items-baseline justify-between">
            <span className="text-2xs text-text-dim uppercase tracking-ultra">Value</span>
            <span className="text-xs text-brass-300 tabular">
              {formatCurrency(client.estimatedAnnualValue)}
            </span>
          </div>
        ) : null}

        {client.territory && (
          <div className="mt-2 text-2xs text-text-dim truncate">{client.territory}</div>
        )}
      </Link>

      {canWrite && next && (
        <button
          onClick={(e) => { e.stopPropagation(); onAdvance(); }}
          className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 p-1 text-text-dim hover:text-brass-300 hover:bg-bg-hover rounded-sm transition-all"
          aria-label={`Advance to ${PIPELINE_LABELS[next]}`}
          title={`Advance → ${PIPELINE_LABELS[next]}`}
        >
          <ArrowRight size={12} />
        </button>
      )}
    </div>
  );
}

function nextStage(s: PipelineStage): PipelineStage | null {
  const order: PipelineStage[] = ['lead', 'qualified', 'specified', 'quoted', 'awarded', 'closed_won'];
  const i = order.indexOf(s);
  if (i === -1 || i === order.length - 1) return null;
  return order[i + 1];
}
